"use client"

import * as React from "react"
import { Slot } from "@radix-ui/react-slot"
import { PanelLeft } from "lucide-react"

import { cn } from "@/lib/utils"
import { Button } from "@/components/ui/button"
import { Input } from "@/components/ui/input"
import { Separator } from "@/components/ui/separator"
import {
  Sheet,
  SheetContent,
  SheetDescription,
  SheetHeader,
  SheetTitle,
} from "@/components/ui/sheet"
import { useSidebar, SIDEBAR_WIDTH_MOBILE } from "@/components/ui/sidebar-context"
import {
  SIDEBAR_WIDTH_LARGE_STEP_PX,
  SIDEBAR_WIDTH_MAX_PX,
  SIDEBAR_WIDTH_MIN_PX,
  SIDEBAR_WIDTH_STEP_PX,
} from "@/lib/sidebar-width"

/** Expanded desktop sidebar width comes from `:root` `--sidebar-width`. */
const sidebarExpandedWidthClass = "sidebar-shell-expanded"
const sidebarWidthTransitionClass =
  "transition-[width,flex-basis] duration-200 ease-out motion-reduce:transition-none group-data-[sidebar-resizing=true]/sidebar-wrapper:duration-0 group-data-[sidebar-resizing=true]/sidebar-wrapper:transition-none"

const Sidebar = React.forwardRef<
  HTMLDivElement,
  React.ComponentProps<"div"> & {
    side?: "left" | "right"
    variant?: "sidebar" | "floating" | "inset"
    collapsible?: "offcanvas" | "icon" | "none"
    rightBorder?: boolean
    headerOffset?: string
  }
>(
  (
    {
      side = "left",
      variant = "sidebar",
      collapsible = "offcanvas",
      rightBorder = false,
      headerOffset,
      className,
      children,
      ...props
    },
    ref
  ) => {
    const { isMobile, isResizing, state, openMobile, setOpenMobile } = useSidebar()

    if (collapsible === "none") {
      return (
        <div
          className={cn(
            "flex h-full flex-shrink-0 flex-col bg-sidebar text-sidebar-foreground",
            sidebarExpandedWidthClass,
            rightBorder && "border-r border-sidebar-border",
            className
          )}
          ref={ref}
          {...props}
        >
          {children}
        </div>
      )
    }

    if (isMobile) {
      return (
        <Sheet open={openMobile} onOpenChange={setOpenMobile} modal={false} {...props}>
          <SheetContent
            data-sidebar="sidebar"
            data-mobile="true"
            className="bg-sidebar p-0 text-sidebar-foreground shadow-none motion-reduce:animate-none [&>button]:size-11 [&>button]:top-2 [&>button]:right-2"
            hideCloseButton
            onInteractOutside={(event) => {
              // Let the original trigger toggle once instead of dismissing then reopening.
              if ((event.target as HTMLElement)?.closest('[data-mobile-menu-trigger]')) {
                event.preventDefault()
              }
            }}
            onCloseAutoFocus={(event) => {
              event.preventDefault()
              document.querySelector<HTMLButtonElement>('[data-mobile-menu-trigger]')?.focus()
            }}
            style={
              {
                width: SIDEBAR_WIDTH_MOBILE,
              } as React.CSSProperties
            }
            side={side}
          >
            <SheetHeader className="sr-only">
              <SheetTitle>Sidebar</SheetTitle>
              <SheetDescription>Displays the mobile sidebar.</SheetDescription>
            </SheetHeader>
            <div className="flex h-full w-full flex-col">{children}</div>
          </SheetContent>
        </Sheet>
      )
    }

    if (headerOffset) {
      return (
        <div
          ref={ref}
          className={cn(
            "group peer relative flex hidden min-h-0 flex-shrink-0 flex-col text-sidebar-foreground md:flex",
            sidebarExpandedWidthClass,
            sidebarWidthTransitionClass,
            "group-data-[collapsible=icon]:!w-[var(--sidebar-width-icon)] group-data-[collapsible=icon]:!flex-none",
            rightBorder && "border-r border-sidebar-border",
            className
          )}
          data-state={state}
          data-collapsible={state === "collapsed" && !isResizing ? collapsible : ""}
          data-variant={variant}
          data-side={side}
          {...props}
        >
          <div
            data-sidebar="sidebar"
            className="flex h-full w-full min-w-0 max-w-full flex-col bg-sidebar"
          >
            {children}
          </div>
        </div>
      )
    }

    return (
      <div
        ref={ref}
        className="group peer hidden text-sidebar-foreground md:block"
        data-state={state}
        data-collapsible={state === "collapsed" && !isResizing ? collapsible : ""}
        data-variant={variant}
        data-side={side}
      >
        {/* This is what handles the sidebar gap on desktop */}
        <div
          className={cn(
            "relative bg-transparent",
            sidebarWidthTransitionClass,
            sidebarExpandedWidthClass,
            "group-data-[collapsible=offcanvas]:!w-0 group-data-[collapsible=offcanvas]:!flex-none",
            "group-data-[side=right]:rotate-180",
            variant === "floating" || variant === "inset"
              ? "group-data-[collapsible=icon]:!w-[calc(var(--sidebar-width-icon)_+_theme(spacing.4))] group-data-[collapsible=icon]:!flex-none"
              : "group-data-[collapsible=icon]:!w-[var(--sidebar-width-icon)] group-data-[collapsible=icon]:!flex-none"
          )}
        />
        <div
          className={cn(
            "fixed z-10 hidden md:flex",
            "transition-[left,right,width,flex-basis] duration-200 ease-out motion-reduce:transition-none group-data-[sidebar-resizing=true]/sidebar-wrapper:duration-0 group-data-[sidebar-resizing=true]/sidebar-wrapper:transition-none",
            sidebarExpandedWidthClass,
            "inset-y-0 h-svh",
            side === "left"
              ? "left-0 group-data-[collapsible=offcanvas]:left-[calc(var(--sidebar-width)*-1)]"
              : "right-0 group-data-[collapsible=offcanvas]:right-[calc(var(--sidebar-width)*-1)]",
            // Adjust the padding for floating and inset variants.
            variant === "floating" || variant === "inset"
              ? "p-2 group-data-[collapsible=icon]:!w-[calc(var(--sidebar-width-icon)_+_theme(spacing.4)_+2px)] group-data-[collapsible=icon]:!flex-none"
              : "group-data-[collapsible=icon]:!w-[var(--sidebar-width-icon)] group-data-[collapsible=icon]:!flex-none group-data-[side=left]:border-r group-data-[side=right]:border-l",
            rightBorder && side === "left" && "border-r border-sidebar-border",
            rightBorder && side === "right" && "border-l border-sidebar-border",
            className
          )}
          {...props}
        >
          <div
            data-sidebar="sidebar"
            className="flex h-full w-full min-w-0 max-w-full flex-col bg-sidebar group-data-[variant=floating]:rounded-lg group-data-[variant=floating]:border group-data-[variant=floating]:border-sidebar-border group-data-[variant=floating]:shadow"
          >
            {children}
          </div>
        </div>
      </div>
    )
  }
)
Sidebar.displayName = "Sidebar"

const SidebarTrigger = React.forwardRef<
  React.ElementRef<typeof Button>,
  React.ComponentProps<typeof Button>
>(({ className, onClick, ...props }, ref) => {
  const { toggleSidebar } = useSidebar()

  return (
    <Button
      ref={ref}
      data-sidebar="trigger"
      variant="ghost"
      size="iconXs"
      className={className}
      onClick={(event) => {
        onClick?.(event)
        toggleSidebar()
      }}
      {...props}
    >
      <PanelLeft />
      <span className="sr-only">Toggle Sidebar</span>
    </Button>
  )
})
SidebarTrigger.displayName = "SidebarTrigger"

const SidebarRail = React.forwardRef<
  HTMLDivElement,
  React.ComponentProps<"div">
>(({ className, ...props }, ref) => {
  const {
    applyPointerWidth,
    beginResize,
    finishResize,
    isMobile,
    isResizing,
    open,
    persistWidth,
    previewDragWidth,
    resetWidth,
    width,
  } = useSidebar()
  const dragOriginRef = React.useRef<{ side: "left" | "right"; origin: number } | null>(null)
  const lastXRef = React.useRef(0)
  const previewDragWidthRef = React.useRef(previewDragWidth)
  const finishResizeRef = React.useRef(finishResize)
  const detachListenersRef = React.useRef<(() => void) | null>(null)
  previewDragWidthRef.current = previewDragWidth
  finishResizeRef.current = finishResize

  const widthFromClientX = (clientX: number) => {
    const drag = dragOriginRef.current
    if (!drag) return width
    return drag.side === "left" ? clientX - drag.origin : drag.origin - clientX
  }

  const stopResize = (clientX: number) => {
    if (!dragOriginRef.current) return
    const nextWidth = widthFromClientX(clientX)
    dragOriginRef.current = null
    detachListenersRef.current?.()
    detachListenersRef.current = null
    finishResizeRef.current(nextWidth)
  }

  React.useEffect(() => {
    return () => {
      detachListenersRef.current?.()
      detachListenersRef.current = null
    }
  }, [])

  const startResize = (event: React.PointerEvent<HTMLDivElement>) => {
    if (isMobile || event.button !== 0 || dragOriginRef.current) return
    const shell = event.currentTarget.closest("[data-side]") as HTMLElement | null
    const side = shell?.getAttribute("data-side") === "right" ? "right" : "left"
    const rect = (shell ?? event.currentTarget).getBoundingClientRect()
    dragOriginRef.current = { side, origin: side === "left" ? rect.left : rect.right }
    lastXRef.current = event.clientX
    beginResize()

    const onPointerMove = (moveEvent: PointerEvent) => {
      if (!dragOriginRef.current) return
      if (moveEvent.buttons === 0) {
        stopResize(moveEvent.clientX)
        return
      }
      lastXRef.current = moveEvent.clientX
      previewDragWidthRef.current(widthFromClientX(moveEvent.clientX))
    }
    const onPointerUp = (upEvent: PointerEvent) => {
      stopResize(upEvent.clientX)
    }
    const onAbort = () => {
      stopResize(lastXRef.current)
    }

    window.addEventListener("pointermove", onPointerMove)
    window.addEventListener("pointerup", onPointerUp)
    window.addEventListener("pointercancel", onPointerUp)
    window.addEventListener("mouseup", onAbort)
    window.addEventListener("blur", onAbort)
    document.addEventListener("visibilitychange", onAbort)
    detachListenersRef.current = () => {
      window.removeEventListener("pointermove", onPointerMove)
      window.removeEventListener("pointerup", onPointerUp)
      window.removeEventListener("pointercancel", onPointerUp)
      window.removeEventListener("mouseup", onAbort)
      window.removeEventListener("blur", onAbort)
      document.removeEventListener("visibilitychange", onAbort)
    }

    event.preventDefault()
  }

  const handleKeyDown = (event: React.KeyboardEvent<HTMLDivElement>) => {
    if (isMobile) return
    const step = event.shiftKey ? SIDEBAR_WIDTH_LARGE_STEP_PX : SIDEBAR_WIDTH_STEP_PX
    if (event.key === "ArrowLeft" || event.key === "ArrowRight") {
      event.preventDefault()
      if (!open && event.key === "ArrowLeft") return
      if (!open && event.key === "ArrowRight") {
        applyPointerWidth(Math.max(width, SIDEBAR_WIDTH_MIN_PX))
        persistWidth()
        return
      }
      applyPointerWidth(width + (event.key === "ArrowRight" ? step : -step))
      persistWidth()
      return
    }
    if (event.key === "Home") {
      event.preventDefault()
      applyPointerWidth(SIDEBAR_WIDTH_MIN_PX)
      persistWidth()
      return
    }
    if (event.key === "End") {
      event.preventDefault()
      applyPointerWidth(SIDEBAR_WIDTH_MAX_PX)
      persistWidth()
    }
  }

  return (
    <div
      ref={ref}
      {...props}
      data-sidebar="rail"
      data-resizing={isResizing ? "true" : undefined}
      role="separator"
      aria-orientation="vertical"
      aria-label="Resize navigation"
      aria-expanded={open}
      aria-valuemin={SIDEBAR_WIDTH_MIN_PX}
      aria-valuemax={SIDEBAR_WIDTH_MAX_PX}
      aria-valuenow={open ? width : undefined}
      tabIndex={isMobile ? -1 : 0}
      title="Drag to resize"
      onPointerDown={startResize}
      onDoubleClick={() => {
        if (isMobile) return
        resetWidth()
      }}
      onKeyDown={handleKeyDown}
      className={cn(
        // Straddle the existing sidebar edge — do not paint a second full-height rule.
        "group/rail absolute inset-y-0 z-20 hidden w-5 translate-x-1/2 touch-none select-none group-data-[side=left]:right-0 group-data-[side=right]:left-0 group-data-[side=right]:-translate-x-1/2 sm:flex",
        "cursor-col-resize outline-none",
        className
      )}
    >
      <div
        aria-hidden
        className="pointer-events-none absolute left-1/2 top-1/2 z-10 flex -translate-x-1/2 -translate-y-1/2 items-center justify-center"
      >
        <div
          className={cn(
            "h-28 w-1 shrink-0 rounded-full bg-muted-foreground/50 opacity-0 transition-opacity duration-150",
            "group-hover/rail:opacity-100 group-focus-visible/rail:opacity-100",
            isResizing && "opacity-100"
          )}
        />
      </div>
    </div>
  )
})
SidebarRail.displayName = "SidebarRail"

const SidebarInset = React.forwardRef<
  HTMLDivElement,
  React.ComponentProps<"main">
>(({ className, ...props }, ref) => {
  return (
    <main
      ref={ref}
      className={cn(
        "relative flex h-full min-h-0 w-full min-w-0 flex-1 flex-col bg-background",
        "md:peer-data-[variant=inset]:m-2 md:peer-data-[state=collapsed]:peer-data-[variant=inset]:ml-2 md:peer-data-[variant=inset]:ml-0 md:peer-data-[variant=inset]:rounded-xl md:peer-data-[variant=inset]:shadow",
        className
      )}
      {...props}
    />
  )
})
SidebarInset.displayName = "SidebarInset"

const SidebarInput = React.forwardRef<
  React.ElementRef<typeof Input>,
  React.ComponentProps<typeof Input>
>(({ className, ...props }, ref) => {
  return (
    <Input
      ref={ref}
      data-sidebar="input"
      className={cn(
        "h-8 w-full bg-background shadow-none focus-visible:ring-2 focus-visible:ring-sidebar-ring",
        className
      )}
      {...props}
    />
  )
})
SidebarInput.displayName = "SidebarInput"

const SidebarHeader = React.forwardRef<
  HTMLDivElement,
  React.ComponentProps<"div">
>(({ className, ...props }, ref) => {
  return (
    <div
      ref={ref}
      data-sidebar="header"
      className={cn("flex flex-col gap-2 p-2", className)}
      {...props}
    />
  )
})
SidebarHeader.displayName = "SidebarHeader"

const SidebarFooter = React.forwardRef<
  HTMLDivElement,
  React.ComponentProps<"div">
>(({ className, ...props }, ref) => {
  return (
    <div
      ref={ref}
      data-sidebar="footer"
      className={cn("flex flex-col gap-2 p-2", className)}
      {...props}
    />
  )
})
SidebarFooter.displayName = "SidebarFooter"

const SidebarSeparator = React.forwardRef<
  React.ElementRef<typeof Separator>,
  React.ComponentProps<typeof Separator>
>(({ className, ...props }, ref) => {
  return (
    <Separator
      ref={ref}
      data-sidebar="separator"
      className={cn("mx-2 w-auto bg-sidebar-border", className)}
      {...props}
    />
  )
})
SidebarSeparator.displayName = "SidebarSeparator"

const SidebarContent = React.forwardRef<
  HTMLDivElement,
  React.ComponentProps<"div">
>(({ className, ...props }, ref) => {
  return (
    <div
      ref={ref}
      data-sidebar="content"
      className={cn(
        "flex min-h-0 flex-1 flex-col gap-2 overflow-auto [scrollbar-gutter:stable] group-data-[collapsible=icon]:overflow-hidden group-data-[collapsible=icon]:[scrollbar-gutter:auto]",
        className
      )}
      {...props}
    />
  )
})
SidebarContent.displayName = "SidebarContent"

const SidebarGroup = React.forwardRef<
  HTMLDivElement,
  React.ComponentProps<"div">
>(({ className, ...props }, ref) => {
  return (
    <div
      ref={ref}
      data-sidebar="group"
      className={cn(
        "relative flex w-full min-w-0 flex-col p-2",
        className
      )}
      {...props}
    />
  )
})
SidebarGroup.displayName = "SidebarGroup"

const SidebarGroupLabel = React.forwardRef<
  HTMLDivElement,
  React.ComponentProps<"div"> & { asChild?: boolean }
>(({ className, asChild = false, ...props }, ref) => {
  const Comp = asChild ? Slot : "div"

  return (
    <Comp
      ref={ref}
      data-sidebar="group-label"
      className={cn(
        "flex h-8 shrink-0 items-center rounded-md px-2 text-xs font-medium text-sidebar-foreground/70 outline-none ring-sidebar-ring transition-[margin,opacity] duration-200 ease-linear focus-visible:ring-2 [&>svg]:size-4 [&>svg]:shrink-0",
        "group-data-[collapsible=icon]:-mt-8 group-data-[collapsible=icon]:opacity-0",
        className
      )}
      {...props}
    />
  )
})
SidebarGroupLabel.displayName = "SidebarGroupLabel"

const SidebarGroupAction = React.forwardRef<
  HTMLButtonElement,
  React.ComponentProps<"button"> & { asChild?: boolean }
>(({ className, asChild = false, ...props }, ref) => {
  const Comp = asChild ? Slot : "button"

  return (
    <Comp
      ref={ref}
      data-sidebar="group-action"
      className={cn(
        "absolute right-3 top-3.5 flex aspect-square w-5 items-center justify-center rounded-md p-0 text-sidebar-foreground outline-none ring-sidebar-ring transition-transform hover:bg-sidebar-accent hover:text-sidebar-accent-foreground focus-visible:ring-2 [&>svg]:size-4 [&>svg]:shrink-0",
        // Increases the hit area of the button on mobile.
        "after:absolute after:-inset-2 after:md:hidden",
        "group-data-[collapsible=icon]:hidden",
        className
      )}
      {...props}
    />
  )
})
SidebarGroupAction.displayName = "SidebarGroupAction"

const SidebarGroupContent = React.forwardRef<
  HTMLDivElement,
  React.ComponentProps<"div">
>(({ className, ...props }, ref) => (
  <div
    ref={ref}
    data-sidebar="group-content"
    className={cn("w-full text-sm", className)}
    {...props}
  />
))
SidebarGroupContent.displayName = "SidebarGroupContent"

export {
  SidebarMenu,
  SidebarMenuAction,
  SidebarMenuBadge,
  SidebarMenuButton,
  SidebarMenuItem,
  SidebarMenuSkeleton,
  SidebarMenuSub,
  SidebarMenuSubButton,
  SidebarMenuSubItem,
} from "@/components/ui/sidebar-menu"

export { SidebarProvider, useSidebar } from "@/components/ui/sidebar-context"

export {
  Sidebar,
  SidebarContent,
  SidebarFooter,
  SidebarGroup,
  SidebarGroupAction,
  SidebarGroupContent,
  SidebarGroupLabel,
  SidebarHeader,
  SidebarInput,
  SidebarInset,
  SidebarRail,
  SidebarSeparator,
  SidebarTrigger,
}
